// src/views/ShoparoundView.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  FaBoxOpen,
  FaChevronDown,
  FaClock,
  FaDollarSign,
  FaEnvelope,
  FaFilter,
  FaListAlt,
  FaLocationArrow,
  FaPlus,
  FaRoute,
  FaSearch,
  FaShoppingBag,
  FaSlidersH,
  FaTimes,
  FaUserLock,
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import AuthDialog from '../components/AuthDialog';
import AddListingModal from './AddListingModal';
import ManageListingsModal from './ManageListingsModal';
import ListingDetailModal from './ListingDetailModal';
import MessagesModal from '../components/MessagesModal';
import './ShoparoundView.css';

function distanceInMiles(from, to) {
  if (!from || !to) return null;

  const lat1 = Number(from.latitude);
  const lon1 = Number(from.longitude);
  const lat2 = Number(to.latitude);
  const lon2 = Number(to.longitude);

  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;

  const toRad = value => (value * Math.PI) / 180;
  const earthMiles = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  return earthMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function normalizePrice(value) {
  const price = Number.parseFloat(value);
  return Number.isFinite(price) ? price : 0;
}

function formatPrice(value) {
  const price = normalizePrice(value);
  return price > 0
    ? price.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : 'Ask seller';
}

function listingImage(item) {
  return item.images?.find(Boolean) || '';
}

const sortOptions = [
  { key: 'nearby', label: 'Nearby', icon: <FaRoute /> },
  { key: 'newest', label: 'Newest', icon: <FaClock /> },
  { key: 'priceLow', label: 'Low price', icon: <FaDollarSign /> },
  { key: 'priceHigh', label: 'High price', icon: <FaDollarSign /> },
];

export default function ShoparoundView() {
  const { user, loading: authLoading } = useAuth();
  const db = getFirestore();

  const [saleItems, setSaleItems] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [blockedByUsers, setBlockedByUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [sorting, setSorting] = useState('nearby');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const requireLogin = action => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    action();
  };

  useEffect(() => {
    if (authLoading) return undefined;

    let active = true;

    async function loadShop() {
      setLoading(true);

      try {
        let loc = null;
        let blockedBy = [];
        let blockedList = [];

        if (user) {
          const uid = user.uid;
          const [profileSnap, blockedBySnap, blockedUsersSnap] = await Promise.all([
            getDoc(doc(db, 'userprofiles', uid)),
            getDoc(doc(db, 'blockedBy', uid)),
            getDoc(doc(db, 'blockedUsers', uid)),
          ]);

          loc = profileSnap.exists() ? profileSnap.data().location || null : null;
          blockedBy = blockedBySnap.exists() ? blockedBySnap.data().blockedByUsers || [] : [];
          blockedList = blockedUsersSnap.exists() ? blockedUsersSnap.data().blockedList || [] : [];
        }

        const listingSnap = await getDocs(
          query(collection(db, 'forsale'), where('status', '==', 'active'))
        );

        const items = listingSnap.docs.map(snapshot => {
          const data = snapshot.data();
          const timestamp = toDate(data.timestamp || data.createdAt || data.timeCreated);
          const distanceMiles = distanceInMiles(loc, data.listingLocation);

          return {
            id: snapshot.id,
            forsaleId: data.forsaleId || snapshot.id,
            receiverId: data.receiverId || data.userId || data.uid || '',
            title: data.title || 'Untitled listing',
            description: data.description || '',
            price: data.price || '',
            city: data.city || '',
            state: data.state || '',
            zipCode: data.zipCode || '',
            category: data.category || data.itemType || '',
            condition: data.condition || '',
            images: [data.image1, data.image2, data.image3, data.imageUrl].filter(Boolean),
            timestamp,
            listingLocation: data.listingLocation || null,
            distanceMiles,
          };
        });

        if (!active) return;
        setUserLocation(loc);
        setBlockedByUsers(blockedBy);
        setBlockedUserIds(blockedList);
        setSaleItems(items);
      } catch (error) {
        console.error('Shoparound load failed:', error);
        if (active) setSaleItems([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadShop();

    return () => {
      active = false;
    };
  }, [authLoading, user, db]);

  const filtered = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    const me = user?.uid || '';

    return saleItems
      .filter(item => !me || item.receiverId !== me)
      .filter(item => !blockedUserIds.includes(item.receiverId))
      .filter(item => !blockedByUsers.includes(item.receiverId))
      .filter(item => {
        if (!needle) return true;
        return [item.title, item.description, item.city, item.state, item.category, item.condition]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      });
  }, [saleItems, searchText, blockedUserIds, blockedByUsers, user]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    let result;
    switch (sorting) {
      case 'newest': result = items.sort((a,b)=>(b.timestamp?.getTime()||0)-(a.timestamp?.getTime()||0)); break;
      case 'priceLow': result = items.sort((a,b)=>normalizePrice(a.price)-normalizePrice(b.price)); break;
      case 'priceHigh': result = items.sort((a,b)=>normalizePrice(b.price)-normalizePrice(a.price)); break;
      case 'nearby':
      default: result = items.sort((a,b)=>(a.distanceMiles??Infinity)-(b.distanceMiles??Infinity));
    }
    return sortDirection === 'desc' ? [...result].reverse() : result;
  }, [filtered, sorting, sortDirection]);

  const selectedSort = sortOptions.find(option => option.key === sorting) || sortOptions[0];
  const activeFilters = searchText.trim() ? 1 : 0;

  return (
    <>
      <Helmet>
        <title>Shoparound | Pool 4 Real</title>
        <meta
          name="description"
          content="Browse billiards gear, cues, tables, accessories, and local pool listings on Pool 4 Real."
        />
      </Helmet>

      <main className="p4r-shop-shell">
        <header className="p4r-shop-topbar">
          <div className="p4r-shop-title-wrap">
            <p className="p4r-shop-kicker">Marketplace</p>
            <h1>Shoparound</h1>
          </div>

          <div className="p4r-shop-actions" aria-label="Shop actions">
            <button
              type="button"
              className="p4r-shop-icon-btn p4r-shop-primary-icon"
              onClick={() => requireLogin(() => setShowAdd(true))}
              aria-label="Add listing"
              title="Add listing"
            >
              <FaPlus />
            </button>

            <button
              type="button"
              className="p4r-shop-icon-btn"
              onClick={() => requireLogin(() => setShowManage(true))}
              aria-label="My listings"
              title="My listings"
            >
              <FaListAlt />
            </button>

            <button
              type="button"
              className="p4r-shop-icon-btn"
              onClick={() => requireLogin(() => setShowMessages(true))}
              aria-label="Messages"
              title="Messages"
            >
              <FaEnvelope />
            </button>

            <button
              type="button"
              className={`p4r-shop-icon-btn ${showFilters ? 'active' : ''}`}
              onClick={() => { setSortDirection((value) => value === 'asc' ? 'desc' : 'asc'); setShowFilters(true); }}
              aria-label="Filters"
              title="Filters"
            >
              <FaSlidersH />
              {activeFilters > 0 && <span className="p4r-shop-dot" />}
            </button>
          </div>
        </header>

        <nav className="p4r-shop-section-menu" aria-label="Shoparound sections">
          <button type="button" className="active" onClick={() => { setShowAdd(false); setShowManage(false); setShowMessages(false); }}>Browse</button>
          <button type="button" onClick={() => requireLogin(() => setShowAdd(true))}>Sell an item</button>
          <button type="button" onClick={() => requireLogin(() => setShowManage(true))}>My listings</button>
          <button type="button" onClick={() => requireLogin(() => setShowMessages(true))}>Messages</button>
        </nav>

        <section className="p4r-shop-quickbar" aria-label="Marketplace controls">
          <div className="p4r-shop-search-wrap">
            <FaSearch />
            <input
              type="search"
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              placeholder="Search gear"
              aria-label="Search listings"
            />
            {searchText && (
              <button type="button" onClick={() => setSearchText('')} aria-label="Clear search">
                <FaTimes />
              </button>
            )}
          </div>

          <button
            type="button"
            className="p4r-shop-sort-pill"
            onClick={() => { setSortDirection((value) => value === 'asc' ? 'desc' : 'asc'); setShowFilters(true); }}
            aria-expanded={showFilters}
          >
            {selectedSort.icon}
            <span>{selectedSort.label} {sortDirection === 'asc' ? '↑' : '↓'}</span>
            <FaChevronDown className={showFilters ? 'open' : ''} />
          </button>
        </section>

        {showFilters && (
          <section className="p4r-shop-filter-panel">
            <div className="p4r-shop-filter-head">
              <FaFilter />
              <span>Sort</span>
            </div>

            <div className="p4r-shop-chip-row">
              {sortOptions.map(option => (
                <button
                  key={option.key}
                  type="button"
                  className={sorting === option.key ? 'active' : ''}
                  onClick={() => { if (sorting === option.key) setSortDirection((value) => value === 'asc' ? 'desc' : 'asc'); else { setSorting(option.key); setSortDirection('asc'); } }}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {!user && (
          <section className="p4r-shop-guest-strip">
            <FaUserLock />
            <span>Browse listings. Sign in to open items, post, manage, or message sellers.</span>
            <button type="button" onClick={() => setShowLoginModal(true)}>Sign in</button>
          </section>
        )}

        <section className="p4r-shop-feed" aria-busy={loading}>
          {loading && (
            <div className="p4r-shop-loading-grid">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="p4r-shop-skeleton" />
              ))}
            </div>
          )}

          {!loading && sorted.length === 0 && (
            <div className="p4r-shop-empty">
              <FaBoxOpen />
              <h2>No listings found</h2>
              <button type="button" onClick={() => requireLogin(() => setShowAdd(true))}>
                Add listing
              </button>
            </div>
          )}

          {!loading && sorted.length > 0 && (
            <div className="p4r-shop-grid">
              {sorted.map(item => {
                const image = listingImage(item);
                const miles = item.distanceMiles;

                return (
                  <article
                    key={item.id}
                    className="p4r-shop-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => requireLogin(() => setDetailItem(item))}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        requireLogin(() => setDetailItem(item));
                      }
                    }}
                  >
                    <div className="p4r-shop-photo-wrap">
                      {image ? (
                        <img src={image} alt={item.title} loading="lazy" />
                      ) : (
                        <div className="p4r-shop-photo-fallback">
                          <FaShoppingBag />
                        </div>
                      )}
                      <div className="p4r-shop-price-badge">{formatPrice(item.price)}</div>
                    </div>

                    <div className="p4r-shop-card-body">
                      <h2>{item.title}</h2>

                      <div className="p4r-shop-card-meta">
                        {miles != null ? (
                          <span><FaLocationArrow /> {Math.max(1, Math.round(miles))} mi</span>
                        ) : userLocation ? (
                          <span><FaRoute /> Distance unavailable</span>
                        ) : (
                          <span><FaRoute /> Sign in for distance</span>
                        )}

                        {(item.city || item.state) && (
                          <span>{[item.city, item.state].filter(Boolean).join(', ')}</span>
                        )}
                      </div>

                      {(item.category || item.condition) && (
                        <div className="p4r-shop-tags">
                          {item.category && <span>{item.category}</span>}
                          {item.condition && <span>{item.condition}</span>}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <AddListingModal show={showAdd} onClose={() => setShowAdd(false)} />
      <ManageListingsModal show={showManage} onClose={() => setShowManage(false)} />
      <MessagesModal show={showMessages} onClose={() => setShowMessages(false)} />

      {detailItem && (
        <ListingDetailModal
          saleItem={detailItem}
          onClose={() => setDetailItem(null)}
        />
      )}

      <AuthDialog
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        subtitle="Sign in to sell equipment, manage listings, and contact sellers."
      />
    </>
  );
}
